import { Button, Spinner } from 'flowbite-react';
import useRazorpay, { RazorpayOptions } from 'react-razorpay';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { APP_ENDPOINT, RAZORPAY_APP_ID } from 'src/config/env.config';
import { useMutation, useQueryClient } from 'react-query';
import { cancelPaymentById, completePaymentById, createPaymentForInterviewOrPrep } from 'src/apis/payment';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendTrackingEvent } from 'src/utils/gtm';
import { get } from 'lodash';
import { formatAmountToPriceDispplay } from 'src/utils/helper';
import moment from 'moment';
import { getValidJapAmount } from 'shared-utils';

interface Props {
	discountedPrice?: number;
	originalPrice: number;
	isJapEnabled: boolean;
	expiry?: Date;
	onClose: () => void;
	serviceName: string;
	btnText: string;
	isForInterview?: boolean;
	handleScheduleInterviewForPaidUser?: () => void;
	prepPlanId?: string;
}

const BuyProgramForInterviewOrPrep = ({
	discountedPrice,
	isJapEnabled,
	originalPrice,
	expiry,
	serviceName,
	btnText,
	isForInterview,
	onClose,
	handleScheduleInterviewForPaidUser,
	prepPlanId,
}: Props) => {
	const queryClient = useQueryClient();
	const location = useLocation();
	const Razorpay = useRazorpay();
	const { user: session } = useUserContext();
	const toast = useToast();
	const navigate = useNavigate();

	const createPaymentFn = useMutation(['createPaymentForInterviewOrPrep'], createPaymentForInterviewOrPrep, {
		onSuccess(data, variables) {
			const finalPrice = variables.amount;
			const paymentId = data.payment?._id || '';
			if (finalPrice != null && finalPrice > 0) {
				const options: RazorpayOptions = {
					key: RAZORPAY_APP_ID,
					amount: String(finalPrice),
					currency: 'INR',
					name: 'LevelUp',
					description: `Transaction to buy a Service ${serviceName}`,
					image: `${APP_ENDPOINT}/logo-razorpay.svg`,
					callback_url: `${APP_ENDPOINT}/`,
					order_id: data.payment?.orderId || '',
					handler: () => {
						completePaymentFn.mutate({ id: paymentId, isForInterviewOrPrep: true });
					},
					prefill: {
						name: session?.name,
						email: session?.email,
						contact: session?.phone || '',
					},
					notes: {
						address: 'Levelup Corporate Office',
					},
					theme: {
						color: '#3399cc',
					},
				};
				const rzpay = new Razorpay(options);
				rzpay.on('payment.failed', function () {
					cancelPaymentFn.mutate(paymentId);
				});
				rzpay.open();
			} else {
				completePaymentFn.mutate({ id: paymentId, isForInterviewOrPrep: true });
			}
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const cancelPaymentFn = useMutation(['cancelPaymentById'], cancelPaymentById, {
		onSuccess() {
			queryClient.refetchQueries(['verifyCookie']);
			queryClient.refetchQueries(['getPaymentStatusForInterviewOrPrep']);
			toast({
				title: 'Payment Failed.',
				type: 'error',
			});
		},
		onError(error: Error) {
			queryClient.refetchQueries(['verifyCookie']);
			queryClient.refetchQueries(['getPaymentStatusForInterviewOrPrep']);
		},
	});

	const completePaymentFn = useMutation(['completePaymentById'], completePaymentById, {
		onSuccess() {
			toast({
				title: 'Payment Complete.',
				type: 'success',
			});
			queryClient.refetchQueries(['verifyCookie']);
			queryClient.refetchQueries(['getPaymentStatusForInterviewOrPrep']);
			if (isForInterview) {
				handleScheduleInterviewForPaidUser && handleScheduleInterviewForPaidUser();
				onClose();
				// navigate('/dashboard');
				navigate('/my-applications');
			} else {
				navigate('/prepare');
			}
		},
		onError(error: Error) {
			queryClient.refetchQueries(['verifyCookie']);
			queryClient.refetchQueries(['getPaymentStatusForInterviewOrPrep']);
		},
	});

	const handlePayment = async () => {
		createPaymentFn.mutate({
			amount: getValidJapAmount({ originalPrice, discountedPrice, expiry }),
			isForInterview: !!isForInterview,
			prepPlanId,
		});
	};

	const isLoading = createPaymentFn.isLoading || cancelPaymentFn.isLoading || completePaymentFn.isLoading;

	const priceDisplay =
		expiry && moment().isAfter(expiry) ? (
			<p className="p-1">{formatAmountToPriceDispplay(originalPrice)}/-</p>
		) : discountedPrice !== undefined && discountedPrice < originalPrice ? (
			<p className="p-1">
				<s>{formatAmountToPriceDispplay(originalPrice)}</s> {formatAmountToPriceDispplay(discountedPrice)}/-
			</p>
		) : (
			<p className="p-1">{formatAmountToPriceDispplay(originalPrice)}/-</p>
		);

	return (
		<Button
			color="purplePlaced"
			className="w-full min-w-[256px] focus:z-0"
			onClick={() => {
				const eventLabel = isForInterview ? 'schedule_pay_now' : 'prepplan_pay_now';
				sendTrackingEvent({
					event: eventLabel,
					eventLabel: location.pathname,
				});
				handlePayment();
			}}
			disabled={!isJapEnabled}
		>
			{isLoading ? <Spinner /> : btnText}
			{priceDisplay}
		</Button>
	);
};

export default BuyProgramForInterviewOrPrep;
